import React, { useContext, useState } from "react";
import Modal from "react-modal";
import {
    ActivityMessageRow,
    AddMessageWrapper,
    CloseButton,
    ColumnData,
    ColumnHeading,
    DetailSpan,
    ExplanationBlock,
    HeaderActions,
    HeaderContent,
    HeadingsContainer,
    HeadingWrapper,
    HorizontalLayout,
    InfoBlock,
    InputElement,
    InputGroupButton,
    Label,
    MessageContent,
    MessageCreatedAt,
    MessageDetails,
    MessageHistoryWrapper,
    MessageText,
    MessageUser,
    ModalHeader,
    MultiDetailRow,
    NoUpdates,
    PartsListingWrapper,
    POStatus,
    PricingDetailRow,
    PricingExplanation,
    PurchaseOrderNumber,
    PurchaseOrderStatusIcon,
    PurchaseOrderTitle,
    SingleDetailRow,
    StatusBarWrapper,
    TabButton,
    TableDataRowWrapper,
    TableDataWrapper,
    TableHeadingsWrapper,
    Value,
    VerticalLayout
} from "./styled";
import { Button } from "components";
import { AppContext } from "hocs/withAppContext";
import { colors } from "colors";
import { addPurchaseOrderUpdate, generatePDF } from "../service";
import { displayStatus } from "../consts";

const modalCustomStyle = {
    content: {
        maxWidth: "900px",
        padding: "0 0 0 0",
        margin: "120px auto",
        borderRadius: "8px",
        boxShadow: "0 1px 0 0 #e5e5ea",
        left: 0,
        right: 0,
    }
};

function daysAgo(dateObj) {
    if (typeof dateObj === 'string') {
        dateObj = new Date(dateObj);
    }
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dateObj.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function getFlexDirectionByMessageType(messageType) {
    messageType = 'auto';
    switch(messageType) {
        case 'auto':
            return 'row';
        case 'manual':
        default:
            return 'column';
    }
}

/**
 * @return {string}
 */
const CurrencySymbol = () => {
    const { currencySymbol } = useContext(AppContext);
    return currencySymbol;
};


const RequestDetails = ({ purchaseOrder: po }) => {
    return (
        <VerticalLayout padding="20px 10px 20px 15px">
            <MultiDetailRow padding="0 0 10px 0">
                <SingleDetailRow padding="0 15px">
                    <DetailSpan>Vendor</DetailSpan>
                    <DetailSpan fontBold>{po.vendor ? po.vendor.businessName : ''}</DetailSpan>
                </SingleDetailRow>
                <SingleDetailRow padding="0 15px">
                    <DetailSpan>Due Date</DetailSpan>
                    <DetailSpan fontBold>{formatDate(po.dueDate)}</DetailSpan>
                </SingleDetailRow>
                <SingleDetailRow padding="0 15px">
                    <DetailSpan>Added By</DetailSpan>
                    <DetailSpan fontBold breakWord>{po.requesterEmail}</DetailSpan>
                </SingleDetailRow>
                <SingleDetailRow padding="0 15px">
                    <DetailSpan>Date Created</DetailSpan>
                    <DetailSpan fontBold>{formatDate(po.createdAt)}</DetailSpan>
                </SingleDetailRow>
                <SingleDetailRow padding="0 15px">
                    <DetailSpan>Total Cost</DetailSpan>
                    <DetailSpan fontBold><CurrencySymbol />{po.totalCost || '-'}</DetailSpan>
                </SingleDetailRow>
            </MultiDetailRow>
            <SingleDetailRow padding="0 15px" margin="10px 0 0 0">
                <DetailSpan>Description</DetailSpan>
                <DetailSpan>{po.description || 'No description given'}</DetailSpan>
            </SingleDetailRow>
        </VerticalLayout>
    );
};

export const MessageHistory = (props) => {
    if (!props.messages.length) {
        return <NoUpdates>No updates yet</NoUpdates>;
    }
    const messageItems = props.messages.map((m, index) => {
        const flexDirection = getFlexDirectionByMessageType(m.messageType);
        return (
            <ActivityMessageRow key={index} messageType={m.messageType}>
                <PurchaseOrderStatusIcon className={m.status}>
                    <span className={`circle ${m.status}`}/>
                </PurchaseOrderStatusIcon>
                <MessageDetails>
                    <MessageContent flexDirection={flexDirection}>
                        <MessageUser>{m.name}</MessageUser>
                        <MessageText messageType={m.messageType} flexDirection={flexDirection}>{m.text}</MessageText>
                    </MessageContent>
                    <MessageCreatedAt>{m.daysAgo}</MessageCreatedAt>
                </MessageDetails>
            </ActivityMessageRow>
        );
    }
    );
    return <MessageHistoryWrapper>{messageItems}</MessageHistoryWrapper>;
};

const ActivityTab = ({ purchaseOrder: po, roleId }) => {
    const [messages, setMessages] = useState(po.updates || []);
    const [newUpdate, setNewUpdate] = useState('');

    const mlist = messages.map(obj => {
        let newObj = { ...obj };
        if (obj.user && obj.user.firstName) {
            newObj.name = obj.user.firstName + obj.user.lastName;
        }
        else {
            newObj.name = obj.email;
        }
        const autoMsgDetectionStrings = ['declined this', 'approved this', 'fulfilled this', 'closed this', 'requested this'];
        newObj.messageType = autoMsgDetectionStrings.some(mds => {
            return obj.text.includes(mds);
        }) ? 'auto' : 'manual';

        newObj.status = 'default';
        for(let i=0; i<autoMsgDetectionStrings.length; i++){
            const mds = autoMsgDetectionStrings[i];
            if (obj.text.includes(mds)) {
                if (mds === 'requested this') {
                    newObj.status = 'awaiting';
                }
                else {
                    newObj.status = mds.replace(' this', '');
                }
                break;
            }
        }

        // workflow triggered: if 'work flow' in message
        if (obj.text.indexOf('work flow') !== -1 && !obj.user) {
            newObj.senderType = 'workflow';
            newObj.messageType = 'auto';
        }
        // internal user: user key present and account type is not requester
        else if (obj.user && obj.user.userAccountType !== 4) {
            newObj.senderType = 'internalUser';
        }
        // public requester: email key present and user key not,
        // or user present but account type is of requester
        else if ((obj.email && !obj.user) || (obj.user && obj.user.userAccountType === 4)) {
            newObj.senderType = 'requester';
        }
        newObj.daysAgo = daysAgo(newObj.createdAt) + " days ago";
        return newObj;
    });

    const newUpdateCopy = newUpdate;

    return (
        <VerticalLayout>
            <MessageHistory messages={mlist} />
            <AddMessageWrapper>
                <InputElement
                    placeholder="Write a message..."
                    required={true}
                    onChange={e => setNewUpdate(e.target.value)}
                    value={newUpdate}
                >
                </InputElement>
                <InputGroupButton
                    onClick={
                        () => {
                            setNewUpdate('');
                            addPurchaseOrderUpdate(roleId, po.requesterEmail, po.objectId, newUpdateCopy)
                                .then(setMessages);
                          }
                    }
                >
                    Send
                </InputGroupButton>
            </AddMessageWrapper>
        </VerticalLayout>
    );
};

const AdditionalInfo = ({ purchaseOrder: po }) => {
    return <VerticalLayout padding="20px 30px 20px 0">
        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Requisitioner</DetailSpan>
                <DetailSpan>{po.requisitioner}</DetailSpan>
            </SingleDetailRow>

            <SingleDetailRow>
                <DetailSpan>Shipping Method</DetailSpan>
                <DetailSpan>{po.shippingMethod}</DetailSpan>
            </SingleDetailRow>

            <SingleDetailRow>
                <DetailSpan>F.O.B Point</DetailSpan>
                <DetailSpan>{po.fobPoint}</DetailSpan>
            </SingleDetailRow>

            <SingleDetailRow>
                <DetailSpan>Notes</DetailSpan>
                <DetailSpan>{po.notes}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>
    </VerticalLayout>
};

const RequesterInformation = ({ purchaseOrder: po }) => {
    return <VerticalLayout padding="30px 0 10px 0">
        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Company Name</DetailSpan>
                <DetailSpan>{po.companyName}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Address</DetailSpan>
                <DetailSpan>{po.companyStreet}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>City</DetailSpan>
                <DetailSpan>{po.companyCity}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>State</DetailSpan>
                <DetailSpan>{po.companyState}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>Zip</DetailSpan>
                <DetailSpan>{po.companyZip}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Phone</DetailSpan>
                <DetailSpan>{po.companyPhone}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>Fax</DetailSpan>
                <DetailSpan>{po.companyFax}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>
    </VerticalLayout>
};

const ShippingInformation = ({ purchaseOrder: po }) => {
    return <VerticalLayout padding="30px 0 10px 30px">
        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Ship To Name</DetailSpan>
                <DetailSpan>{po.shippingName}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Company Name</DetailSpan>
                <DetailSpan>{po.shippingCompany}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Address</DetailSpan>
                <DetailSpan>{po.shippingStreet}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>City</DetailSpan>
                <DetailSpan>{po.shippingCity}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>State</DetailSpan>
                <DetailSpan>{po.shippingState}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>Zip</DetailSpan>
                <DetailSpan>{po.shippingZip}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>

        <MultiDetailRow>
            <SingleDetailRow>
                <DetailSpan>Phone</DetailSpan>
                <DetailSpan>{po.shippingPhone}</DetailSpan>
            </SingleDetailRow>
            <SingleDetailRow>
                <DetailSpan>Fax</DetailSpan>
                <DetailSpan>{po.shippingFax}</DetailSpan>
            </SingleDetailRow>
        </MultiDetailRow>
    </VerticalLayout>
};

const PartsList = (props) => {
    if (!props.parts || !props.parts.length) {
        return null;
    }

    return props.parts.map((part, index) => (
            <TableDataRowWrapper key={index}>
                <ColumnData dataRow flex={{ key: 'id' }}>{index + 1}</ColumnData>
                <ColumnData nameCol dataRow flex={{ key: 'name' }}>{part.stringPartName}</ColumnData>
                <ColumnData dataRow flex={{ key: 'cost' }}><CurrencySymbol/>{part.stringPartCost}</ColumnData>
                <ColumnData dataRow flex={{ key: 'qty' }}>{part.numberPartQuantity}</ColumnData>
                <ColumnData dataRow flex={{ key: 'desc' }}>{part.stringPartDescription}</ColumnData>
                <ColumnData dataRow flex={{ key: 'price' }}><CurrencySymbol/>{part.numberPartQuantity * part.numberPartCost}
                </ColumnData>
            </TableDataRowWrapper>
        )
    );
};

function formatDate(dateObj) {
    var monthNames = [
        "January", "February", "March",
        "April", "May", "June", "July",
        "August", "September", "October",
        "November", "December"
    ];

    if (dateObj) {
        let date;
        if (dateObj.iso)
            date = new Date(dateObj.iso);
        else
            date = new Date(dateObj);
        return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getUTCFullYear()}`;
    }
    else {
        return '-';
    }
}

function getStatusBarColor(status) {
    let colorProps = {};

    switch (status) {
        case 'awaiting':
            colorProps.color = colors.burntYellow;
            colorProps.bgColor = "rgba(224, 170, 8, 0.1)";
            break;
        case 'approved':
            colorProps.color = colors.shamrockGreen;
            colorProps.bgColor = "rgba(5, 131, 57, 0.1)";
            break;
        case 'declined':
            colorProps.color = colors.strawberry;
            colorProps.bgColor = "rgba(207, 19, 36, 0.1)";
            break;
        case 'partially-fulfilled':
            colorProps.color = colors.brightBlue;
            colorProps.border = `2px solid ${colors.brightBlue}`;
            break;
        case 'fulfilled':
            colorProps.color = colors.brightBlue;
            colorProps.bgColor = "rgba(0, 123, 255, 0.1)";
            break;
        default:
            break;
    }
    return colorProps;
}

const DetailTab = ({ role, purchaseOrder: po }) => {
    return (
        <React.Fragment>
            <HorizontalLayout borderTopNull padding="15px 0 15px 30px">
                <Button
                    margin="0 10px 0 0"
                    theme='portal'
                    onClick={() => generatePDF(role, po)}
                >
                    <span>Generate PDF</span>
                </Button>
            </HorizontalLayout>
            <StatusBarWrapper coloring={getStatusBarColor(po.status)}>
                <POStatus>{displayStatus[po.status]}</POStatus>
            </StatusBarWrapper>
            <RequestDetails purchaseOrder={po} />
            <PartsListingWrapper>
                <TableHeadingsWrapper>
                    <ColumnHeading flex={{ key: 'id' }}>#</ColumnHeading>
                    <ColumnHeading flex={{ key: 'name' }}>Item</ColumnHeading>
                    <ColumnHeading flex={{ key: 'cost' }}>Cost</ColumnHeading>
                    <ColumnHeading flex={{ key: 'qty' }}>Quantity</ColumnHeading>
                    <ColumnHeading flex={{ key: 'desc' }}>Description</ColumnHeading>
                    <ColumnHeading flex={{ key: 'price' }}>Price</ColumnHeading>
                </TableHeadingsWrapper>
                <TableDataWrapper>
                    <PartsList parts={po.parts} />
                </TableDataWrapper>
                <PricingExplanation>
                    <ExplanationBlock>
                        <PricingDetailRow>
                            <Label>Subtotal</Label><Value><CurrencySymbol />{po.subtotal}</Value>
                        </PricingDetailRow>
                        <PricingDetailRow>
                            <Label>Sales Tax</Label><Value><CurrencySymbol />{po.tax}</Value>
                        </PricingDetailRow>
                        <PricingDetailRow>
                            <Label>Shipping</Label> <Value><CurrencySymbol />{po.shippingCost}</Value>
                        </PricingDetailRow>
                        <PricingDetailRow>
                            <Label>Other Costs</Label><Value><CurrencySymbol />{po.otherCost}</Value>
                        </PricingDetailRow>
                        <PricingDetailRow>
                            <Label>Total</Label><Value><CurrencySymbol />{po.totalCost}</Value>
                        </PricingDetailRow>
                    </ExplanationBlock>
                </PricingExplanation>
            </PartsListingWrapper>
            <InfoBlock>
                <HeadingsContainer>
                    <HeadingWrapper>Requester Information</HeadingWrapper>
                    <HeadingWrapper>Shipping Information</HeadingWrapper>
                </HeadingsContainer>
                <HorizontalLayout>
                    <RequesterInformation purchaseOrder={po} />
                    <ShippingInformation purchaseOrder={po} />
                </HorizontalLayout>
            </InfoBlock>
            <InfoBlock>
                <HeadingsContainer padding="30px 0 10px 30px">
                    <HeadingWrapper>Additional Info</HeadingWrapper>
                </HeadingsContainer>
                <HorizontalLayout>
                    <AdditionalInfo purchaseOrder={po} />
                </HorizontalLayout>
            </InfoBlock>
        </React.Fragment>
    );
};

export const RequestDetailsModal = ({ activeRequest, tabToActivate, ...props }) => {
    const { role } = useContext(AppContext);
    const roleId = role.objectId;

    const [activeTab, setActiveTab] = useState(tabToActivate || 'details');
    const isDetailTabActive = activeTab === 'details';
    return (
        <Modal style={modalCustomStyle} {...props} ariaHideApp={false} >
            <ModalHeader>
                <HeaderContent>
                    <PurchaseOrderTitle>{activeRequest.title}</PurchaseOrderTitle>
                    <PurchaseOrderNumber>#{activeRequest.purchaseOrderNumber}</PurchaseOrderNumber>
                </HeaderContent>
                <HeaderActions>
                    <CloseButton onClick={() => props.onRequestClose()} />
                </HeaderActions>
            </ModalHeader>
            <HorizontalLayout borderTopNull borderBottom padding="0 0 0 20px">
                <TabButton
                    padding="16px 14px 15px 14px"
                    onClick={() => setActiveTab('details')}
                    isActive={isDetailTabActive.toString()}
                >
                    Details
                </TabButton>
                <TabButton
                    padding="16px 14px 15px 14px"
                    margin="0 0 0 10px"
                    onClick={() => setActiveTab('activity')}
                    isActive={(!isDetailTabActive).toString()}
                >
                    Activity
                </TabButton>
            </HorizontalLayout>
            {isDetailTabActive && <DetailTab purchaseOrder={activeRequest} role={role} />}
            {!isDetailTabActive && <ActivityTab purchaseOrder={activeRequest} roleId={roleId} />}
        </Modal>
    );
};

export default RequestDetailsModal;
